import { useRef } from 'react'

import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import type { CtaProps } from '~/components/atoms/Ctas/Ctas'
import Ctas from '~/components/atoms/Ctas/Ctas'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import type { TextProps } from '~/components/atoms/Text/Text'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import { ComponentTheme } from '~/components/themes'

import styles from './DownloadHero.module.css'

export interface DownloadHeroProps {
  title: string
  buttonText: string
  theme: ComponentTheme
  image: ImageProps
  ctas: CtaProps[]
  imageLeft?: boolean
  pretitle?: string
  testID?: string
  titleTag?: TextProps['Tag']
  moduleId?: string
}

export default function DownloadHero({
  title,
  buttonText,
  pretitle,
  theme,
  image,
  ctas,
  imageLeft,
  testID,
  titleTag = 'h1',
  moduleId,
}: DownloadHeroProps) {
  const content = useRef<HTMLDivElement>(null)

  const buttonHandler = () => {
    content.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    <Fixture theme={theme ? theme : ComponentTheme.black} id={moduleId}>
      <div className={styles.base} data-testid={testID}>
        <div
          className={classNames(styles.inner, imageLeft && styles.innerFlipped)}
        >
          <div className={styles.media}>
            {image && image.asset && (
              <Image
                asset={image.asset}
                objectFit="cover"
                sizes="(max-width: 799px) 80vw, 50vw"
                alt={image.alt}
              />
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.contentInner}>
              <VerticalPadder>
                {pretitle && (
                  <Text
                    size={TextSizes.primaryTitleMed}
                    className={styles.pretitle}
                    Tag="p"
                  >
                    {pretitle}
                  </Text>
                )}
                {title && (
                  <Text
                    size={TextSizes.primaryTitleXLrg}
                    Tag={titleTag}
                    className={styles.title}
                    dataNoSnippet={true}
                  >
                    {title}
                  </Text>
                )}
                {buttonText && (
                  // Legacy button - replaced by CTAs but left here whilst we transition to using CTAs in the CMS
                  <Button
                    iconLeft={true}
                    button={{
                      type: ButtonTypes.button,
                      onClick: buttonHandler,
                    }}
                    variant={
                      theme === ComponentTheme.primary
                        ? ButtonVariants.primaryDark
                        : ButtonVariants.primaryLight
                    }
                  >
                    {buttonText}
                  </Button>
                )}
                {ctas && <Ctas ctas={ctas} theme={theme} />}
              </VerticalPadder>
            </div>
          </div>
        </div>
      </div>
      <div ref={content}></div>
    </Fixture>
  )
}
